import React, { useEffect, useMemo, useState } from "react";
import { Check, MapPin, Handshake, Square, Zap, Scale, Lock, ChevronDown, Phone, ArrowLeft, Repeat, ShieldCheck, Target } from "lucide-react";
import content from "@/data/content.json";

const ACCENT = "#0f9d6e";
const NAVY = "#1e2532";
const API = process.env.REACT_APP_BACKEND_URL;

const fb = (...args) => {
  if (typeof window !== "undefined" && typeof window.fbq === "function") window.fbq(...args);
};

const scrollToFunnel = () => {
  const el = document.getElementById("funnel");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Cta = ({ testId, className = "" }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={scrollToFunnel}
    className={`btn-solid inline-flex items-center justify-center px-8 py-4 text-[15px] font-bold uppercase tracking-[0.04em] ${className}`}
  >
    <span>Testkontingent anfragen</span>
  </button>
);

const Logo = () => (
  <span className="flex shrink-0 flex-col">
    <span className="flex items-start gap-[2px] leading-none">
      <span className="font-display text-[21px] font-bold leading-none tracking-[-0.03em]">energy</span>
      <span className="font-display text-[21px] font-bold leading-none tracking-[-0.03em] text-[#3ecf9b]">leads</span>
      <span className="mt-[1px] text-[8px] font-bold leading-none text-white/60">TM</span>
    </span>
    <span className="mt-[4px] text-[8.5px] font-semibold uppercase leading-none tracking-[0.13em] text-white/55">
      Mehr Projekte. Mehr Energie.
    </span>
  </span>
);

const LpHeader = () => (
  <header data-testid="lp-header" className="fixed top-0 left-0 right-0 z-50 bg-[#1e2532] text-white">
    <div className="mx-auto flex h-[68px] max-w-[1180px] items-center justify-between px-6 md:px-10">
      <Logo />
      <a href="tel:+4921714013650" data-testid="lp-phone" className="flex items-center gap-2 text-[15px] font-bold">
        <Phone size={16} className="text-[#3ecf9b]" />
        02171 4013650
      </a>
    </div>
  </header>
);

/* ---------- Hero ---------- */
const Hero = () => (
  <section className="bg-[#1e2532] pt-[110px] pb-16 text-white md:pb-20" data-testid="lp-hero">
    <div className="mx-auto grid max-w-[1180px] grid-cols-1 items-center gap-10 px-6 md:px-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
      <div>
        <span className="label-kicker">Für Projektentwickler, die Batteriespeicher bauen</span>
        <h1 className="mt-6 text-4xl font-bold leading-[1.12] sm:text-5xl lg:text-[52px]">
          Du willst mehr Großbatteriespeicher- oder{" "}
          <span style={{ color: "#3ecf9b" }}>Schwarmspeicher-Projekte</span> realisieren?
        </h1>
        <p className="mt-6 max-w-[580px] text-[16.5px] leading-[1.75] text-white/75">
          Spar dir mühsame Kaltakquise und endlose Recherche. Wir liefern dir geprüfte Verpachtungsanfragen direkt
          von Eigentümern mit konkretem Verpachtungsinteresse — aus Industrie- und Gewerbegebieten, per
          Stromnetzkarte vorgeprüft, exklusiv für dich.
        </p>
        <div className="mt-8">
          <Cta testId="lp-cta-hero" />
          <p className="mt-4 text-[13px] text-white/55">
            Keine Mindestlaufzeit · Einstieg ab 10 Anfragen · Antwort innerhalb von 24 Stunden
          </p>
        </div>
        <ul className="mt-8 flex flex-col gap-3 sm:gap-4">
          {[
            "Geprüfte Eigentümerkontakte aus Industrie & Gewerbe",
            "Keine Mindestabnahme & keine Laufzeitbindung",
            "Schneller Projektstart durch vorqualifizierte Standorte",
          ].map((t) => (
            <li key={t} className="flex items-center gap-3 text-[15px] font-medium text-white/85">
              <span className="flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-full bg-[#0f9d6e]">
                <Check size={12} strokeWidth={3} className="text-white" />
              </span>
              {t}
            </li>
          ))}
        </ul>
      </div>
      <div className="overflow-hidden rounded-[18px] border border-white/10">
        <img
          src="/img/lp-bess-hero3.webp"
          alt="Batteriespeicher-Container auf Freifläche mit Photovoltaik-Anlage im Hintergrund"
          width="1264"
          height="848"
          loading="eager"
          decoding="async"
          className="h-full w-full object-cover"
        />
      </div>
    </div>
  </section>
);

/* ---------- Logo-Band unter dem Hero ---------- */
const LogoBand = () => {
  const logos = content.referenzen.logoStrip.slice(0, 14);
  return (
    <section className="border-b border-[#e6e8e6] bg-[#f5f6f4] py-10" data-testid="lp-logoband">
      <p className="text-center text-[12.5px] font-bold uppercase tracking-[0.12em] text-[#565f70]">
        Firmen, die schon mit energyleads zusammengearbeitet haben
      </p>
      <div className="marquee-wrap relative mt-6 overflow-hidden py-2">
        <div className="marquee-track">
          {[...logos, ...logos].map((logo, i) => (
            <div key={i} className="flex h-[48px] w-[124px] shrink-0 items-center justify-center px-3 opacity-75 sm:h-[56px] sm:w-[186px] sm:px-6">
              <img
                src={logo.src}
                alt={`Logo ${logo.name}`}
                loading="lazy"
                decoding="async"
                className="h-[calc(var(--lh)*0.78)] w-auto max-w-full object-contain sm:h-[var(--lh)]"
                style={{ "--lh": `${logo.h || 33}px`, filter: "grayscale(1) contrast(1.05)" }}
              />
            </div>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#f5f6f4] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#f5f6f4] to-transparent" />
      </div>
    </section>
  );
};

/* ---------- Produkt ---------- */
const PRODUKT = [
  [MapPin, "Lage im Gewerbe- oder Industriegebiet", "Dort ist das Genehmigungsverfahren deutlich einfacher als im Außenbereich. Keine Flächen im Außenbereich, keine landwirtschaftlichen Flächen."],
  [Handshake, "Eigentümer will verpachten", "Der Eigentümer hat sich selbst gemeldet und im persönlichen Gespräch bestätigt, dass er seine Fläche für einen Batteriespeicher verpachten möchte."],
  [Square, "Ausreichend Stellfläche", "Freie, ebene, befahrbare Fläche für die Aufstellung der Speichereinheiten — geprüft im Eigentümergespräch."],
  [Zap, "Netzinfrastruktur vorgeprüft", "Lage im Stromnetz per Stromnetzkarte geprüft. Bevorzugt Standorte in der Nähe von Umspannwerken, Trafostationen und Netzverknüpfungspunkten."],
  [Scale, "Klare Eigentumsverhältnisse", "Ein Eigentümer, der entscheiden kann. Keine ungeklärten Erbengemeinschaften."],
  [Lock, "Exklusiv für dich", "Jede Anfrage geht an genau ein Partnerunternehmen. Kein Weiterverkauf, kein Wettbewerb um dieselbe Fläche."],
];

const Produkt = () => (
  <section className="bg-[#f5f6f4] py-16 md:py-20" data-testid="lp-produkt">
    <div className="mx-auto max-w-[1180px] px-6 md:px-10">
      <h2 className="max-w-[760px] text-3xl font-bold leading-[1.18] text-[#1e2532] md:text-[40px]">
        Keine Masse, sondern <span style={{ color: ACCENT }}>Qualität</span> — das steckt in jeder Anfrage
      </h2>
      <p className="mt-5 max-w-[680px] text-[16.5px] leading-[1.75] text-[#565f70]">
        Keine Adresse aus einem Formular, sondern ein vorbereitetes Gespräch. Jede Verpachtungsanfrage, die wir dir
        übergeben, erfüllt folgende Kriterien:
      </p>
      <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {PRODUKT.map(([Icon, h, t]) => (
          <div key={h} className="rounded-[16px] border border-[#dfe2e0] border-t-[3px] border-t-[#0f9d6e] bg-white p-6 md:p-7">
            <span className="flex h-[42px] w-[42px] items-center justify-center rounded-[10px] bg-[#e7f5ef]">
              <Icon size={20} className="text-[#0b7d57]" />
            </span>
            <h3 className="mt-4 text-[17px] font-semibold leading-[1.35] text-[#1e2532]">{h}</h3>
            <p className="mt-3 text-[14.5px] leading-[1.7] text-[#565f70]">{t}</p>
          </div>
        ))}
      </div>
      <div className="mt-10 text-center">
        <Cta testId="lp-cta-produkt" />
      </div>
    </div>
  </section>
);

/* ---------- Varianten ---------- */
const Varianten = () => (
  <section className="bg-white py-16 md:py-20" data-testid="lp-varianten">
    <div className="mx-auto max-w-[1180px] px-6 md:px-10">
      <h2 className="max-w-[760px] text-3xl font-bold leading-[1.18] text-[#1e2532] md:text-[40px]">
        Schwarmspeicher oder Großbatteriespeicher — <span style={{ color: ACCENT }}>Du wählst</span>
      </h2>
      <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="rounded-[16px] border border-[#dfe2e0] bg-[#f5f6f4] p-7 md:p-8">
          <h3 className="text-[20px] font-bold text-[#1e2532]">Schwarmspeicher-Standorte</h3>
          <p className="mt-4 text-[15px] leading-[1.75] text-[#565f70]">
            Für Projekte von 250 kW bis ca. 1,5 MW pro Standort. Ein bis zwei Speichereinheiten auf
            Gewerbegrundstücken. Niederspannungsanschluss meist möglich, schnelle Netzrückmeldung, in reinen
            Gewerbe- und Industriegebieten oft vereinfachtes Verfahren.
          </p>
          <p className="mt-4 text-[15px] leading-[1.75] text-[#565f70]">
            <strong className="text-[#1e2532]">Typische Flächen:</strong> Lager- und Logistikhöfe,
            Produktionsstandorte mit Außenfläche, Hallen mit Stellfläche, Gewerbegrundstücke mit Trafostation.
          </p>
        </div>
        <div className="rounded-[16px] border border-[#dfe2e0] bg-[#f5f6f4] p-7 md:p-8">
          <h3 className="text-[20px] font-bold text-[#1e2532]">Großbatteriespeicher-Standorte</h3>
          <p className="mt-4 text-[15px] leading-[1.75] text-[#565f70]">
            Für Projekte ab mehreren MW. Größere zusammenhängende Flächen, Mittel- oder Hochspannungsanschluss. Auf
            Wunsch gezielt mit Umspannwerksnähe innerhalb von 1.000 Metern.
          </p>
          <p className="mt-4 text-[15px] leading-[1.75] text-[#565f70]">
            <strong className="text-[#1e2532]">Typische Flächen:</strong> Brachliegende Industrieareale, große
            Gewerbegrundstücke, Flächen mit vorhandenem Netzanschluss.
          </p>
        </div>
      </div>
      <p className="mt-7 text-[15.5px] font-medium leading-[1.7] text-[#1e2532]">
        Du legst fest, welche Variante, welche Bundesländer und welche Mindestkriterien. Wir liefern nur, was zu
        deiner Pipeline passt.
      </p>
      <div className="mt-8 text-center">
        <Cta testId="lp-cta-varianten" />
      </div>
    </div>
  </section>
);

/* ---------- Warum wir ---------- */
const Warum = () => (
  <section className="bg-[#f5f6f4] py-16 md:py-20" data-testid="lp-warum">
    <div className="mx-auto grid max-w-[1180px] grid-cols-1 items-center gap-10 px-6 md:px-10 lg:grid-cols-2 lg:gap-14">
      <div>
        <h2 className="text-3xl font-bold leading-[1.18] text-[#1e2532] md:text-[40px]">
          Von Projektentwicklern <span style={{ color: ACCENT }}>für Projektentwickler</span>
        </h2>
        <p className="mt-6 text-[15.5px] leading-[1.75] text-[#565f70]">
          energyleads ist kein Leadportal. Wir kommen selbst aus der Projektentwicklung für Batteriespeicher — von
          der Flächensicherung über die Netzanfrage bis zum verkaufsfertigen Projekt — und wissen deshalb aus
          eigener Praxis, was gute Flächen und Standorte ausmacht. Bei unseren Partnern liegt die Abschlussquote
          beim Pachtvertrag bei bis zu 40 Prozent.
        </p>
        <p className="mt-4 text-[15.5px] leading-[1.75] text-[#565f70]">
          Die Akquise, die wir dafür aufgebaut haben — rund 30 Flächenscouts bundesweit, eigene Kaltakquise, eigene
          digitale Kampagnen — liefert täglich mehr qualifizierte Anfragen, als ein einzelnes Unternehmen
          verarbeiten kann. Diese Anfragen geben wir an Partnerunternehmen weiter.
        </p>
        <p className="mt-4 text-[15.5px] font-semibold leading-[1.75] text-[#1e2532]">
          Der Unterschied: Wir wissen aus eigener Praxis, was eine gute Fläche ausmacht. Ein Leadportal weiß das
          nicht.
        </p>
        <ul className="mt-7 space-y-3">
          {[
            "Wir kennen die Kriterien, weil wir selbst danach entwickeln",
            "Wir sprechen mit jedem Eigentümer persönlich, bevor du es tust",
            "Wir liefern dauerhaft — kein Einmalkauf, sondern ein planbarer Zufluss",
          ].map((t) => (
            <li key={t} className="flex gap-3 text-[15px] font-medium leading-[1.6] text-[#1e2532]">
              <span className="mt-[2px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#0f9d6e]">
                <Check size={11} strokeWidth={3} className="text-white" />
              </span>
              {t}
            </li>
          ))}
        </ul>
      </div>
      <div className="overflow-hidden rounded-[18px] border border-[#dfe2e0]">
        <img
          src="/img/lp-bess-projekt.webp"
          alt="Luftbild eines Gewerbegebiets mit Batteriespeicher-Containern neben einer Trafostation"
          width="1264"
          height="848"
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
        />
      </div>
    </div>
  </section>
);

/* ---------- Ablauf ---------- */
const ABLAUF = [
  ["01", "Erstgespräch (15 Minuten)", "Du nennst uns Bundesländer, Speichertyp und Volumen pro Monat. Wir sagen dir ehrlich, ob und wie wir liefern können."],
  ["02", "Testkontingent", "Du startest mit 10 bis 20 Anfragen. Keine Mindestlaufzeit, kein Abo. Du prüfst Qualität und Prozess."],
  ["03", "Laufende Lieferung", "Passt es, vereinbaren wir ein dauerhaftes Volumen. Dazu bekommst du unser Gesprächsskript für Eigentümergespräche und Unterstützung bei der Pachtvertragsgestaltung."],
];

const Ablauf = () => (
  <section className="bg-white py-16 md:py-20" data-testid="lp-ablauf">
    <div className="mx-auto max-w-[1180px] px-6 md:px-10">
      <h2 className="text-3xl font-bold leading-[1.18] text-[#1e2532] md:text-[40px]">
        In drei Schritten zur ersten <span style={{ color: ACCENT }}>Anfrage</span>
      </h2>
      <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
        {ABLAUF.map(([n, h, t]) => (
          <div key={n} className="rounded-[16px] border border-[#dfe2e0] bg-[#f5f6f4] p-7">
            <p className="font-display text-[15px] font-bold tracking-[0.14em] text-[#0f9d6e]">{n}</p>
            <h3 className="mt-3 text-[18px] font-semibold text-[#1e2532]">{h}</h3>
            <p className="mt-3 text-[14.5px] leading-[1.7] text-[#565f70]">{t}</p>
          </div>
        ))}
      </div>
      <div className="mt-10 text-center">
        <Cta testId="lp-cta-ablauf" />
      </div>
    </div>
  </section>
);

/* ---------- Zusammenarbeit ---------- */
const ZUSAMMENARBEIT = [
  [Repeat, "Konstantes Volumen statt Einzelfälle", "Wir stimmen mit dir ab, wie viele neue Projekte du pro Monat verarbeiten kannst, und liefern in diesem Rahmen dauerhaft. Kein Kaufen auf Zuruf, kein Warten auf den nächsten Zufall."],
  [ShieldCheck, "Vorgeprüft, nicht nur gesammelt", "Jeder Standort stammt aus einem persönlichen Gespräch mit dem Eigentümer oder Investor. Fläche, Netzverknüpfungspunkt, Eigentumsverhältnisse und Verpachtungs- oder Kaufbereitschaft sind vorab geklärt."],
  [Target, "Abgestimmt auf deine Kriterien", "Region, Flächengröße, Flächentyp und Mindestanforderungen an den Netzanschluss legst du fest. Du erhältst, was zu deiner Projektpipeline passt."],
  [Lock, "Deine Daten bleiben deine Daten", "Kontakte, Flächen und Pipeline gehören dir. Beim Ende der Zusammenarbeit erhältst du einen vollständigen Export."],
];

const Zusammenarbeit = () => (
  <section className="bg-[#f5f6f4] py-16 md:py-20" data-testid="lp-zusammenarbeit">
    <div className="mx-auto max-w-[1180px] px-6 md:px-10">
      <h2 className="max-w-[760px] text-3xl font-bold leading-[1.18] text-[#1e2532] md:text-[40px]">
        Kein Anfragenverkauf. Ein dauerhafter <span style={{ color: ACCENT }}>Zufluss</span>.
      </h2>
      <p className="mt-5 max-w-[720px] text-[16.5px] leading-[1.75] text-[#565f70]">
        Eine gekaufte Einzelanfrage ist nach einem Gespräch verbraucht. Unsere Partner erhalten stattdessen einen
        kontinuierlichen Strom neuer Standort-Verpachtungsanfragen oder auch Kaufanfragen von IAB-Investoren —
        planbar, in abgestimmtem Volumen und in der Region, in der du entwickelst.
      </p>
      <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
        {ZUSAMMENARBEIT.map(([Icon, h, t]) => (
          <div key={h} className="rounded-[16px] border border-[#dfe2e0] border-t-[3px] border-t-[#0f9d6e] bg-white p-7 md:p-8">
            <span className="flex h-[42px] w-[42px] items-center justify-center rounded-[10px] bg-[#e7f5ef]">
              <Icon size={20} className="text-[#0b7d57]" />
            </span>
            <h3 className="mt-4 text-[17px] font-semibold text-[#1e2532]">{h}</h3>
            <p className="mt-3 text-[14.5px] leading-[1.7] text-[#565f70]">{t}</p>
          </div>
        ))}
      </div>
      <div className="mt-10 text-center">
        <Cta testId="lp-cta-zusammenarbeit" />
      </div>
    </div>
  </section>
);

/* ---------- Referenzen ---------- */
const LP_REFS = [
  ["Sunli GmbH / Solarriese", "/img/u-8866226f51.webp", 24, "Ca. 28 Aufstellorte inklusive Netzzusagen für Schwarmspeicher erschlossen. Über 100 aktive Netzanfragen für Großbatteriespeicher."],
  ["Sunnü Energy GmbH", "/img/u-7a50a83509.webp", 38, "5 Pachtflächen mit Potenzial auf 10 MW erschlossen. 3 Netzzusagen mit insgesamt 15 MW erhalten."],
  ["Novatt GmbH", "/img/u-4abd26f7ec.webp", 34, "Ca. 200 MW Batteriespeicherprojekte in die Pipeline aufgenommen."],
];

const Referenzen = () => (
  <section className="bg-[#f5f6f4] py-16 md:py-20" data-testid="lp-referenzen">
    <div className="mx-auto max-w-[1180px] px-6 md:px-10">
      <h2 className="text-3xl font-bold leading-[1.18] text-[#1e2532] md:text-[40px]">
        Partner, die mit unseren <span style={{ color: ACCENT }}>Standorten</span> arbeiten
      </h2>
      <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
        {LP_REFS.map(([name, logo, h, text]) => (
          <div key={name} className="rounded-[16px] border border-[#dfe2e0] border-t-[3px] border-t-[#0f9d6e] bg-white p-6 md:p-7">
            <div className="flex h-[44px] items-center">
              <img src={logo} alt={`Logo ${name}`} loading="lazy" decoding="async" className="w-auto max-w-[160px] object-contain" style={{ height: `${h}px`, filter: "grayscale(1) contrast(1.05)" }} />
            </div>
            <h3 className="mt-4 text-[17px] font-semibold text-[#1e2532]">{name}</h3>
            <p className="mt-3 text-[14.5px] leading-[1.7] text-[#565f70]">{text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Einwände ---------- */
const EINWAENDE = [
  ["„Ich habe schon Leads gekauft, das war nichts.\u201c", "Verstehen wir. Meistens war es ein Portal, das denselben Kontakt an acht Firmen verkauft hat — ohne je mit dem Eigentümer gesprochen zu haben. Bei uns geht jede Anfrage an genau ein Unternehmen, und wir haben jeden Eigentümer vorher persönlich telefonisch qualifiziert. Deshalb starten wir mit einem Testkontingent: Du prüfst die Qualität, bevor du dich bindest."],
  ["„Garantiert ihr den Netzanschluss?\u201c", "Nein — das kann niemand, das entscheidet der Netzbetreiber. Wir prüfen die Netzkarte vorher, schauen auf Umspannwerksnähe und Spannungsebene und schätzen das Potenzial ein. Wer dir eine Garantie verspricht, sagt nicht die Wahrheit."],
  ["„Kann ich nur Flächen aus meiner Region bekommen?\u201c", "Nach Bundesländern filterbar, ja. Kleinräumiger nicht — unsere Akquise läuft bundesweit. Mindestens drei Bundesländer sollten es sein, sonst kommt zu wenig Volumen zustande."],
  ["„Was ist, wenn eine Anfrage nicht erreichbar ist?\u201c", "Sind Telefonnummer und E-Mail nicht erreichbar, wurde die Anfrage doppelt geliefert oder sagt der Eigentümer innerhalb von 48 Stunden ab, ersetzen wir die Anfrage. Das steht so im Vertrag."],
  ["„Seid ihr nicht selbst mein Wettbewerber?\u201c", "Wir entwickeln selbst, ja. Praktisch spielt das keine Rolle: Der Ausbaubedarf ist so groß, dass es nicht am Wettbewerb um Flächen scheitert, sondern an Netzanschlüssen und Kapazität. Unser Geschäft mit Partnern läuft unabhängig davon, wer welches Projekt realisiert."],
];

const Einwaende = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="bg-white py-16 md:py-20" data-testid="lp-einwaende">
      <div className="mx-auto max-w-[860px] px-6 md:px-10">
        <h2 className="text-3xl font-bold leading-[1.18] text-[#1e2532] md:text-[40px]">
          Was du wahrscheinlich gerade <span style={{ color: ACCENT }}>denkst</span>
        </h2>
        <div className="mt-9 divide-y divide-[#dfe2e0] rounded-[16px] border border-[#dfe2e0] bg-white">
          {EINWAENDE.map(([q, a], i) => (
            <div key={q}>
              <button
                type="button"
                data-testid={`lp-faq-${i}`}
                onClick={() => setOpen(open === i ? -1 : i)}
                className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left text-[16px] font-semibold text-[#1e2532]"
              >
                {q}
                <ChevronDown size={18} className={`shrink-0 text-[#0f9d6e] transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && <p className="px-6 pb-6 text-[15px] leading-[1.75] text-[#565f70]">{a}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Ansprechpartner ---------- */
const Ansprechpartner = () => (
  <section className="bg-[#f5f6f4] py-16 md:py-20" data-testid="lp-ansprechpartner">
    <div className="mx-auto flex max-w-[860px] flex-col items-center gap-8 px-6 md:flex-row md:px-10">
      <img
        src="/img/2a84c2f80b.webp"
        alt="Marcel Simons, Kundenservice bei energyleads"
        width="180"
        height="180"
        loading="lazy"
        decoding="async"
        className="h-[150px] w-[150px] rounded-full border-4 border-white object-cover shadow-lg md:h-[180px] md:w-[180px]"
      />
      <div>
        <p className="text-[18px] font-medium leading-[1.7] text-[#1e2532]">
          „Wenn du wissen willst, ob unsere Verpachtungsanfragen für die Errichtung von Batteriespeichern zu deiner
          Pipeline passen, ruf mich an — dann prüfen wir es gemeinsam in 15 Minuten."
        </p>
        <p className="mt-4 text-[15px] font-semibold text-[#1e2532]">Marcel Simons</p>
        <p className="text-[14px] text-[#565f70]">Kundenservice bei energyleads</p>
        <a href="tel:+4921714013650" className="mt-2 inline-flex items-center gap-2 text-[16px] font-bold text-[#0b7d57]">
          <Phone size={15} /> 02171 4013650
        </a>
      </div>
    </div>
  </section>
);

/* ---------- Funnel ---------- */
const STORAGE_KEY = "lp-bess-funnel-v2";

const loadSaved = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch (e) {
    return {};
  }
};

const Funnel = ({ utm }) => {
  const saved = useMemo(loadSaved, []);
  const [step, setStep] = useState(saved.step || 1);
  const [typ, setTyp] = useState(saved.typ || "");
  const [kauf, setKauf] = useState(saved.kauf || "");
  const [form, setForm] = useState({ vorname: "", nachname: "", company: "", email: "", phone: "", consent: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ step, typ, kauf }));
    } catch (e) {}
  }, [step, typ, kauf]);

  const pickTyp = (v) => {
    setTyp(v);
    fb("track", "InitiateCheckout");
    setStep(2);
  };
  const pickKauf = (v) => {
    setKauf(v);
    fb("trackCustom", "FunnelStep", { step: 2 });
    setStep(3);
  };

  const validate = () => {
    const e = {};
    if (!form.vorname.trim()) e.vorname = "Bitte Vornamen angeben.";
    if (!form.nachname.trim()) e.nachname = "Bitte Nachnamen angeben.";
    if (!form.company.trim()) e.company = "Bitte Unternehmen angeben.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email)) e.email = "Bitte eine gültige E-Mail-Adresse angeben.";
    if (form.phone.replace(/\D/g, "").length < 8) e.phone = "Bitte eine Telefonnummer mit mindestens 8 Ziffern angeben.";
    if (!form.consent) e.consent = "Bitte der Datenschutzerklärung zustimmen.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate() || submitting) return;
    setSubmitting(true);
    setServerError("");
    try {
      const res = await fetch(`${API}/api/lp-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: `${form.vorname.trim()} ${form.nachname.trim()}`,
          company: form.company.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          speichertyp: typ,
          kaufanfragen: kauf,
          utm_source: utm.utm_source || "",
          utm_campaign: utm.utm_campaign || "",
          utm_content: utm.utm_content || "",
          page: "lp-bess",
          consent: true,
        }),
      });
      if (!res.ok) throw new Error("request failed");
      fb("trackCustom", "FunnelStep", { step: 3 });
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {}
      setTimeout(() => {
        window.location.href = "/lp/batteriespeicher-leads/danke/";
      }, 250);
    } catch (e) {
      setServerError("Das hat leider nicht geklappt. Versuch es bitte noch einmal oder ruf uns an: 02171 4013650.");
      setSubmitting(false);
    }
  };

  const Back = ({ to }) => (
    <button type="button" onClick={() => setStep(to)} className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#565f70]" data-testid="funnel-back">
      <ArrowLeft size={14} /> Zurück
    </button>
  );

  const field = (key, label, type = "text", half = false) => (
    <div className={half ? "" : "sm:col-span-2"}>
      <label className="mb-1.5 block text-[13px] font-semibold text-[#1e2532]" htmlFor={`lp-${key}`}>
        {label} *
      </label>
      <input
        id={`lp-${key}`}
        data-testid={`funnel-input-${key}`}
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className={`w-full rounded-[10px] border bg-white px-4 py-3 text-[15px] text-[#1e2532] outline-none transition-colors focus:border-[#0f9d6e] ${errors[key] ? "border-[#d9534f]" : "border-[#d7dad8]"}`}
      />
      {errors[key] && <p className="mt-1.5 text-[12.5px] font-medium text-[#d9534f]">{errors[key]}</p>}
    </div>
  );

  const tile = "w-full rounded-[14px] border-2 bg-white p-6 text-left transition-colors hover:border-[#0f9d6e]";

  return (
    <section id="funnel" className="scroll-mt-[80px] bg-[#1e2532] py-16 md:py-20" data-testid="lp-funnel">
      <div className="mx-auto max-w-[740px] px-5 md:px-10">
        <h2 className="text-center text-3xl font-bold leading-[1.18] text-white md:text-[40px]">
          Testkontingent <span style={{ color: "#3ecf9b" }}>anfragen</span>
        </h2>
        <div className="mt-9 rounded-[18px] bg-white p-6 md:p-9">
          <div>
            <p className="text-[12.5px] font-bold uppercase tracking-[0.1em] text-[#565f70]">Schritt {step} von 3</p>
            <div className="mt-2 h-[6px] overflow-hidden rounded-full bg-[#e8eae8]">
              <div className="h-full rounded-full bg-[#0f9d6e] transition-all duration-500" style={{ width: `${(step / 3) * 100}%` }} />
            </div>
          </div>

          {step === 1 && (
            <div className="funnel-step mt-7" data-testid="funnel-step-1">
              <p className="text-[19px] font-bold text-[#1e2532]">Welche Standorte suchst du?</p>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <button type="button" data-testid="funnel-typ-schwarm" onClick={() => pickTyp("Schwarmspeicher")} className={`${tile} ${typ === "Schwarmspeicher" ? "border-[#0f9d6e]" : "border-[#dfe2e0]"}`}>
                  <Zap size={22} className="text-[#0b7d57]" />
                  <span className="mt-3 block text-[17px] font-bold text-[#1e2532]">Schwarmspeicher</span>
                  <span className="mt-1 block text-[13.5px] text-[#565f70]">250 kW bis ca. 1,5 MW</span>
                </button>
                <button type="button" data-testid="funnel-typ-gross" onClick={() => pickTyp("Großbatteriespeicher")} className={`${tile} ${typ === "Großbatteriespeicher" ? "border-[#0f9d6e]" : "border-[#dfe2e0]"}`}>
                  <Square size={22} className="text-[#0b7d57]" />
                  <span className="mt-3 block text-[17px] font-bold text-[#1e2532]">Großbatteriespeicher</span>
                  <span className="mt-1 block text-[13.5px] text-[#565f70]">ab mehreren MW</span>
                </button>
              </div>
              <button type="button" data-testid="funnel-typ-beides" onClick={() => pickTyp("Beides")} className="mt-4 text-[14px] font-semibold text-[#0b7d57] underline underline-offset-2">
                Beides
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="funnel-step mt-7" data-testid="funnel-step-2">
              <p className="text-[19px] font-bold text-[#1e2532]">
                Hast du auch Interesse an Kaufanfragen von IAB-Investoren für deine fertigen Batteriespeicher- oder
                PV-Projekte?
              </p>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <button type="button" data-testid="funnel-kauf-ja" onClick={() => pickKauf("Ja")} className={`${tile} ${kauf === "Ja" ? "border-[#0f9d6e]" : "border-[#dfe2e0]"}`}>
                  <Handshake size={22} className="text-[#0b7d57]" />
                  <span className="mt-3 block text-[17px] font-bold text-[#1e2532]">Ja, interessant</span>
                  <span className="mt-1 block text-[13.5px] text-[#565f70]">Kaufanfragen für fertige Projekte mitnehmen</span>
                </button>
                <button type="button" data-testid="funnel-kauf-nein" onClick={() => pickKauf("Nein")} className={`${tile} ${kauf === "Nein" ? "border-[#0f9d6e]" : "border-[#dfe2e0]"}`}>
                  <Square size={22} className="text-[#0b7d57]" />
                  <span className="mt-3 block text-[17px] font-bold text-[#1e2532]">Nein</span>
                  <span className="mt-1 block text-[13.5px] text-[#565f70]">Nur Verpachtungsanfragen</span>
                </button>
              </div>
              <Back to={1} />
            </div>
          )}

          {step === 3 && (
            <form className="funnel-step mt-7" onSubmit={submit} noValidate data-testid="funnel-step-3">
              <p className="text-[19px] font-bold text-[#1e2532]">Fast geschafft — wohin dürfen wir die Einschätzung schicken?</p>
              <p className="mt-3 rounded-[10px] bg-[#f2f8f5] px-4 py-3 text-[13.5px] text-[#1e2532]">
                {typ} · Kaufanfragen: {kauf}{" "}
                <button type="button" data-testid="funnel-aendern" onClick={() => setStep(1)} className="ml-1 font-bold text-[#0b7d57] underline underline-offset-2">
                  Ändern
                </button>
              </p>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {field("vorname", "Vorname", "text", true)}
                {field("nachname", "Nachname", "text", true)}
                {field("company", "Unternehmen")}
                {field("email", "E-Mail", "email", true)}
                {field("phone", "Telefon", "tel", true)}
              </div>
              <label className="mt-5 flex items-start gap-3 text-[13.5px] leading-[1.6] text-[#565f70]">
                <input type="checkbox" data-testid="funnel-consent" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-[3px] h-4 w-4 accent-[#0f9d6e]" />
                <span>
                  Ich habe die{" "}
                  <a href="/datenschutzerklaerung/" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#0b7d57] underline underline-offset-2">
                    Datenschutzerklärung
                  </a>{" "}
                  gelesen und bin mit der Verarbeitung meiner Daten zur Bearbeitung der Anfrage einverstanden. *
                </span>
              </label>
              {errors.consent && <p className="mt-1.5 text-[12.5px] font-medium text-[#d9534f]">{errors.consent}</p>}
              {serverError && <p className="mt-4 text-[13.5px] font-medium text-[#d9534f]" data-testid="funnel-server-error">{serverError}</p>}
              <button type="submit" data-testid="funnel-submit" disabled={submitting} className="btn-solid mt-6 inline-flex w-full items-center justify-center px-8 py-4 text-[15px] font-bold uppercase tracking-[0.04em] disabled:opacity-60">
                <span>{submitting ? "Wird gesendet …" : "Testkontingent anfragen"}</span>
              </button>
              <p className="mt-4 text-center text-[12.5px] text-[#565f70]">
                Kostenlos und unverbindlich · Antwort innerhalb von 24 Stunden · Keine Mindestlaufzeit
              </p>
              <Back to={2} />
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

/* ---------- Fuß + Sticky ---------- */
const LpFooter = () => (
  <footer className="bg-[#161c27] py-8 pb-24 text-center text-[13px] text-white/55 md:pb-8" data-testid="lp-footer">
    energyleads™ — eine Marke der ONEVOLT Energy GmbH · Borsigstraße 1 · 51381 Leverkusen ·{" "}
    <a href="/impressum/" className="underline underline-offset-2">Impressum</a> ·{" "}
    <a href="/datenschutzerklaerung/" className="underline underline-offset-2">Datenschutz</a>
  </footer>
);

const StickyCta = () => (
  <div className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[#1e2532]/95 p-3 backdrop-blur-sm md:hidden" data-testid="lp-sticky-cta">
    <button type="button" onClick={scrollToFunnel} className="btn-solid flex w-full items-center justify-center px-6 py-3.5 text-[14px] font-bold uppercase tracking-[0.04em]">
      <span>Testkontingent anfragen</span>
    </button>
  </div>
);

export default function LandingBess() {
  const [utm, setUtm] = useState({});

  useEffect(() => {
    document.title = "Batteriespeicher-Leads für Projektentwickler | energyleads";
    const p = new URLSearchParams(window.location.search);
    const u = {
      utm_source: p.get("utm_source") || "",
      utm_campaign: p.get("utm_campaign") || "",
      utm_content: p.get("utm_content") || "",
    };
    try {
      const prev = JSON.parse(sessionStorage.getItem("lp-bess-utm") || "{}");
      const merged = {
        utm_source: u.utm_source || prev.utm_source || "",
        utm_campaign: u.utm_campaign || prev.utm_campaign || "",
        utm_content: u.utm_content || prev.utm_content || "",
      };
      sessionStorage.setItem("lp-bess-utm", JSON.stringify(merged));
      setUtm(merged);
    } catch (e) {
      setUtm(u);
    }
  }, []);

  return (
    <div className="bg-white" data-testid="lp-bess">
      <LpHeader />
      <main>
        <Hero />
        <LogoBand />
        <Produkt />
        <Varianten />
        <Warum />
        <Ablauf />
        <Zusammenarbeit />
        <Referenzen />
        <Einwaende />
        <Ansprechpartner />
        <Funnel utm={utm} />
      </main>
      <LpFooter />
      <StickyCta />
    </div>
  );
}
